import { useEffect, useRef, useState, useMemo } from "react";
import { API_BASE, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { toast } from "sonner";
import {
  Music2,
  Upload as UploadIcon,
  Play,
  Pause,
  Trash2,
  Pencil,
  X,
  Check,
  Loader2,
  Tag,
} from "lucide-react";

const ACCEPT_AUDIO = "audio/mpeg,audio/mp3,audio/mp4,audio/wav,audio/aac,audio/ogg,.mp3,.m4a,.wav,.aac,.ogg";
const MAX_SIZE = 50 * 1024 * 1024;

function fmtDuration(s) {
  if (!s || !isFinite(s)) return "—";
  const m = Math.floor(s / 60);
  const ss = Math.round(s % 60);
  return `${m}:${ss.toString().padStart(2, "0")}`;
}

function fmtSize(b) {
  if (!b) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export default function MusicLibrary() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalError, setGlobalError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadModal, setUploadModal] = useState(null); // {file}
  const [editModal, setEditModal] = useState(null); // {track}
  const [playingId, setPlayingId] = useState(null);
  const [progressById, setProgressById] = useState({});
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);

  const refresh = async () => {
    setGlobalError("");
    try {
      const res = await fetch(`${API_BASE}/music`, { credentials: "include" });
      if (res.status === 401) {
        setGlobalError("Bitte erneut einloggen.");
        return;
      }
      const body = await res.json();
      setTracks(body);
    } catch (e) {
      setGlobalError("Bibliothek konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // ----- Audio playback -----
  useEffect(() => {
    if (!audioRef.current) return;
    const a = audioRef.current;
    const onTime = () => {
      if (!playingId) return;
      setProgressById((p) => ({ ...p, [playingId]: a.currentTime || 0 }));
    };
    const onEnd = () => setPlayingId(null);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, [playingId]);

  const togglePlay = (track) => {
    const a = audioRef.current;
    if (!a) return;
    if (playingId === track.id) {
      a.pause();
      setPlayingId(null);
      return;
    }
    a.src = `${API_BASE}/music/${track.id}/stream`;
    a.play().then(() => setPlayingId(track.id)).catch((e) => {
      toast.error("Wiedergabe fehlgeschlagen: " + (e.message || "unbekannt"));
    });
  };

  // ----- Upload flow -----
  const handlePickFile = (file) => {
    if (!file) return;
    if (file.size > MAX_SIZE) {
      toast.error("Datei größer als 50 MB");
      return;
    }
    setUploadModal({
      file,
      title: file.name.replace(/\.[^.]+$/, ""),
      artist: "",
      tags: "",
    });
  };

  const handleSubmitUpload = async () => {
    if (!uploadModal) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", uploadModal.file);
    form.append("title", uploadModal.title || uploadModal.file.name);
    form.append("artist", uploadModal.artist || "");
    form.append("mood_tags", uploadModal.tags || "");
    try {
      const res = await fetch(`${API_BASE}/music/upload`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!res.ok) {
        const detail = await formatApiErrorDetail(res);
        toast.error("Upload fehlgeschlagen: " + detail);
        return;
      }
      toast.success("Track hochgeladen");
      setUploadModal(null);
      refresh();
    } catch (e) {
      toast.error("Upload fehlgeschlagen: " + (e.message || "Netzwerk"));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (track) => {
    if (!window.confirm(`Track „${track.title}" wirklich löschen?`)) return;
    const res = await fetch(`${API_BASE}/music/${track.id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      toast.error("Löschen fehlgeschlagen");
      return;
    }
    if (playingId === track.id) {
      audioRef.current?.pause();
      setPlayingId(null);
    }
    setTracks((arr) => arr.filter((t) => t.id !== track.id));
    toast.success("Track gelöscht");
  };

  const handleSubmitEdit = async () => {
    if (!editModal) return;
    const { track, title, artist, tags } = editModal;
    const body = {
      title,
      artist,
      mood_tags: tags
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    };
    const res = await fetch(`${API_BASE}/music/${track.id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      toast.error("Änderungen konnten nicht gespeichert werden");
      return;
    }
    setEditModal(null);
    refresh();
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handlePickFile(file);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      <Topbar />
      <audio ref={audioRef} preload="none" />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <header className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Musik-Bibliothek
            </h1>
            <p className="text-sm text-[#9a9aa3] mt-1">
              Deine eigenen Tracks für Hintergrundmusik in Videos.
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_AUDIO}
            className="hidden"
            onChange={(e) => handlePickFile(e.target.files?.[0])}
            data-testid="music-file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-gradient-to-r from-[#ff6b35] to-[#ff8d65] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            data-testid="music-upload-button"
          >
            <UploadIcon size={16} />
            Track hochladen
          </button>
        </header>

        <section
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="rounded-2xl border-2 border-dashed border-[#2a2a30] bg-[#0d0d0e]/40 p-6 sm:p-10 text-center"
          data-testid="music-dropzone"
        >
          <Music2 className="mx-auto text-[#3a3a40] mb-3" size={36} />
          <p className="text-sm text-[#9a9aa3]">
            Ziehe eine Audio-Datei hierher oder klick oben auf{" "}
            <span className="text-white">Track hochladen</span>.
          </p>
          <p className="text-xs text-[#6a6a73] mt-1">
            MP3, M4A, WAV — max 50 MB.
          </p>
        </section>

        {globalError && (
          <div className="rounded-md border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-200">
            {globalError}
          </div>
        )}

        <section data-testid="music-list">
          {loading ? (
            <div className="flex items-center gap-2 text-[#9a9aa3]">
              <Loader2 size={16} className="animate-spin" /> Lade Bibliothek…
            </div>
          ) : tracks.length === 0 ? (
            <div className="rounded-xl border border-[#2a2a30] bg-[#0d0d0e]/60 px-6 py-10 text-center text-sm text-[#9a9aa3]">
              Noch keine Tracks in der Bibliothek.
            </div>
          ) : (
            <ul className="space-y-2">
              {tracks.map((t) => {
                const isPlaying = playingId === t.id;
                const pos = progressById[t.id] || 0;
                const pct = t.duration_sec ? (pos / t.duration_sec) * 100 : 0;
                return (
                  <li
                    key={t.id}
                    className="rounded-xl border border-[#2a2a30] bg-[#0d0d0e] px-4 py-3"
                    data-testid={`music-row-${t.id}`}
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        type="button"
                        onClick={() => togglePlay(t)}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#1f1f23] hover:bg-[#2a2a30] text-white transition-colors"
                        aria-label={isPlaying ? "Pause" : "Abspielen"}
                        data-testid={`music-play-${t.id}`}
                      >
                        {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-white truncate">
                          {t.title}
                        </div>
                        <div className="text-xs text-[#9a9aa3] truncate">
                          {t.artist || "—"} · {fmtDuration(t.duration_sec)} · {fmtSize(t.size)}
                        </div>
                        {t.mood_tags?.length > 0 && (
                          <div className="flex gap-1.5 mt-1.5 flex-wrap">
                            {t.mood_tags.map((tag) => (
                              <span
                                key={tag}
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium text-sky-200/80 bg-sky-500/10 border border-sky-500/30 px-1.5 py-0.5 rounded"
                              >
                                <Tag size={9} />
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setEditModal({
                            track: t,
                            title: t.title,
                            artist: t.artist || "",
                            tags: (t.mood_tags || []).join(", "),
                          })
                        }
                        className="inline-flex items-center justify-center w-9 h-9 rounded-md text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23] transition-colors"
                        aria-label="Bearbeiten"
                        data-testid={`music-edit-${t.id}`}
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(t)}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-md text-[#9a9aa3] hover:text-red-300 hover:bg-red-500/10 transition-colors"
                        aria-label="Löschen"
                        data-testid={`music-delete-${t.id}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                    {isPlaying && (
                      <div className="mt-2 h-1 rounded-full bg-[#1f1f23] overflow-hidden">
                        <div
                          className="h-full bg-[#ff6b35] transition-[width] duration-200"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>

      {/* Upload modal */}
      {uploadModal && (
        <ModalShell title="Track-Details" onClose={() => setUploadModal(null)}>
          <div className="space-y-3">
            <FieldText
              label="Titel"
              value={uploadModal.title}
              onChange={(v) => setUploadModal((s) => ({ ...s, title: v }))}
              testid="music-upload-title"
            />
            <FieldText
              label="Künstler (optional)"
              value={uploadModal.artist}
              onChange={(v) => setUploadModal((s) => ({ ...s, artist: v }))}
              testid="music-upload-artist"
            />
            <FieldText
              label="Stimmungs-Tags"
              placeholder="z. B. chill, ukulele, strand"
              value={uploadModal.tags}
              onChange={(v) => setUploadModal((s) => ({ ...s, tags: v }))}
              testid="music-upload-tags"
            />
            <div className="text-[11px] text-[#6a6a73]">
              Tags helfen später bei der automatischen Musik-Zuordnung pro Szene.
            </div>
          </div>
          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setUploadModal(null)}
              className="h-10 px-4 rounded-md text-sm text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23] transition-colors"
              data-testid="music-upload-cancel"
            >
              Abbrechen
            </button>
            <button
              type="button"
              disabled={uploading}
              onClick={handleSubmitUpload}
              className="h-10 px-4 rounded-md text-sm font-semibold bg-gradient-to-r from-[#ff6b35] to-[#ff8d65] text-white inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="music-upload-submit"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Hochladen
            </button>
          </div>
        </ModalShell>
      )}

      {editModal && (
        <ModalShell title="Track bearbeiten" onClose={() => setEditModal(null)}>
          <div className="space-y-3">
            <FieldText
              label="Titel"
              value={editModal.title}
              onChange={(v) => setEditModal((s) => ({ ...s, title: v }))}
              testid="music-edit-title"
            />
            <FieldText
              label="Künstler"
              value={editModal.artist}
              onChange={(v) => setEditModal((s) => ({ ...s, artist: v }))}
              testid="music-edit-artist"
            />
            <FieldText
              label="Stimmungs-Tags"
              value={editModal.tags}
              onChange={(v) => setEditModal((s) => ({ ...s, tags: v }))}
              testid="music-edit-tags"
            />
          </div>
          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditModal(null)}
              className="h-10 px-4 rounded-md text-sm text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23] transition-colors"
              data-testid="music-edit-cancel"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={handleSubmitEdit}
              className="h-10 px-4 rounded-md text-sm font-semibold bg-[#ff6b35] hover:bg-[#ff8d65] text-white"
              data-testid="music-edit-submit"
            >
              Speichern
            </button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

function ModalShell({ title, children, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="w-full max-w-md rounded-2xl border border-[#2a2a30] bg-[#0a0a0b] shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2a2a30]">
          <h2 className="text-base font-semibold text-white">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23]"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-5">{children}</div>
      </div>
    </div>
  );
}

function FieldText({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block text-sm">
      <span className="block mb-1 text-[#9a9aa3]">{label}</span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full h-10 px-3 rounded-md bg-[#0d0d0e] border border-[#2a2a30] text-white focus:outline-none focus:border-[#ff6b35]"
      />
    </label>
  );
}
